import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import React, { useCallback, useState } from 'react';
import InputMask from 'react-input-mask';

import { Logo } from 'assets/icons';
import { saveWhatsAppContactRequest } from 'services/firebase/whatsappContactService';

import styles from './styles';

const WHATSAPP_NUMBER = '5516997697037';
const WHATSAPP_MESSAGE =
  'Olá! Preciso de ajuda para escolher minha assinatura no Clube da 42.';
const INTRO_MESSAGE =
  'Fala cervejeiro(a), beleza? Precisando de uma ajudinha para escolher a cerveja ideal pra você? Me chama aqui no whats que te ajudo com isso agora mesmo!';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getWhatsAppUrl = () =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

const isMobileDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );

/** Pop-ups após await são bloqueados no mobile; usar navegação na mesma aba. */
const openWhatsApp = () => {
  const url = getWhatsAppUrl();
  if (isMobileDevice()) {
    window.location.assign(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

const WhatsAppContact: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{ email?: string; phone?: string }>({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = useCallback(() => {
    const nextErrors: { email?: string; phone?: string } = {};
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      nextErrors.email = 'Informe seu e-mail';
    } else if (!emailRegex.test(trimmedEmail)) {
      nextErrors.email = 'E-mail inválido';
    }

    const digits = phone.replace(/\D/g, '');
    if (!digits) {
      nextErrors.phone = 'Informe seu telefone';
    } else if (digits.length < 10 || digits.length > 11) {
      nextErrors.phone = 'Telefone inválido';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [email, phone]);

  const handleSubmit = async () => {
    setSubmitError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await saveWhatsAppContactRequest({
        email: email.trim(),
        phone: phone.trim(),
        source: 'floating_whatsapp_button',
        pageUrl: window.location.pathname,
      });
      setIsOpen(false);
      setEmail('');
      setPhone('');
      openWhatsApp();
    } catch (error) {
      console.error('Erro ao salvar contato WhatsApp:', error);
      setSubmitError('Não foi possível enviar seus dados. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggle = () => {
    setIsOpen(prev => !prev);
    setSubmitError('');
    setErrors({});
  };

  return (
    <>
      <IconButton
        onClick={handleToggle}
        aria-label={isOpen ? 'Fechar contato WhatsApp' : 'Falar no WhatsApp'}
        sx={styles.floatingButton}
      >
        {isOpen ? (
          <CloseIcon sx={{ fontSize: 28 }} />
        ) : (
          <WhatsAppIcon sx={{ fontSize: 32 }} />
        )}
      </IconButton>

      {isOpen && (
        <Box sx={styles.modal} role="dialog" aria-label="Contato via WhatsApp">
          <Box sx={styles.header}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar src={Logo} alt="" sx={styles.avatar} />
              <Box sx={styles.onlineDot} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
              >
                Hop Head da Avenida 42
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                online
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleToggle}
              aria-label="Fechar"
              sx={{ color: '#fff' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={styles.body}>
            <Box sx={styles.messageBubble}>
              <Typography variant="body2" sx={{ color: '#1a1008' }}>
                {INTRO_MESSAGE}
              </Typography>
            </Box>

            <TextField
              fullWidth
              required
              placeholder="Email *"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isSubmitting}
              sx={styles.input}
              size="small"
            />

            <InputMask
              mask="(99) 99999-9999"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              maskChar={null}
              disabled={isSubmitting}
            >
              {(
                inputProps: React.InputHTMLAttributes<HTMLInputElement>,
              ): React.ReactElement => {
                const {
                  size: _size,
                  color: _color,
                  disabled: _disabled,
                  ...rest
                } = inputProps;
                return (
                  <TextField
                    {...rest}
                    fullWidth
                    required
                    placeholder="Telefone *"
                    name="phone"
                    variant="outlined"
                    color="primary"
                    error={!!errors.phone}
                    helperText={errors.phone}
                    sx={styles.input}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography component="span" sx={{ fontSize: 18 }}>
                            🇧🇷
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                );
              }}
            </InputMask>

            {submitError && (
              <Typography variant="caption" color="error">
                {submitError}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => {
                handleSubmit();
              }}
              disabled={isSubmitting}
              sx={styles.submitButton}
            >
              {isSubmitting ? (
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              ) : (
                'Chamar no whats'
              )}
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default WhatsAppContact;
